import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getSummaryTransaction } from "./api";
import moment from "moment";
import { Card, CardTitle } from "reactstrap";
import { CustomTable } from "components/UI";
import { SelectInput } from "components/UI";
import { formatNumber } from "components/UI/helpers";
import StatusBadge from "components/UI/StatusBadge";
import MemberListInput from "../utilities/MemberListInput";
import { useDispatch } from "react-redux";
import { getMembers } from "redux/actions/society";
import { ExportCSV } from "../utilities/ExportCSV";
import DaterangeSelector from "components/UI/DaterangeSelector";
import StateListInput from "../utilities/StateListInput";

function SummaryTransactionHistory() {
  const today = moment().format("YYYY-MM-DD");
  const aYearAgo = moment(today).subtract(1, "year").format("YYYY-MM-DD");
  const dispatch = useDispatch();
  const {
    auth: { user, society },
    society: { members },
  } = useSelector((s) => s);

  const [list, setList] = useState([]);
  const [form, setForm] = useState({
    user_id: user.id,
    from: aYearAgo,
    to: today,
  });

  const handleRange = ({ target: { name, value } }) => {
    setForm((p) => ({ ...p, [name]: value }));
  };

  const [selectMembers, setSelectMembers] = useState({});
  const [selectDec, setSelectDec] = useState([]);

  useEffect(() => {
    if (!Object.keys(selectMembers).length) {
      setSelectMembers({ ...user, user_id: user.id });
    }
  }, [form.user_id]);

  const _query = user.id === selectMembers.id ? "all" : "by_member";
  const getHistory = useCallback(() => {
    getSummaryTransaction(
      {
        society_id: society.id,
        member_id: form.state,
        from: form.from,
        to: form.to,
        query_type: _query,
      },
      (d) => {
        setList(d.data);
        const desc = d?.data?.map((e) => e?.account_description);
        var unique = desc.filter(
          (value, index, array) => array.indexOf(value) === index
        );
        setSelectDec(unique);
      }
    );
  }, [society, form.state, form.to, form.from, _query]);

  const _getMembers = useCallback(() => {
    dispatch(getMembers(society.id));
  }, [society.id]);

  useEffect(() => {
    getHistory();
    _getMembers();
  }, [getHistory, getHistory]);

  const tableFields = [
    {
      title: "Date",
      custom: true,
      component: (i) => <div>{moment(i?.created_at).format("YYYY-MM-DD")}</div>,
    },
    { title: "Name", value: "member_name" },
    { title: "Description", value: "account_description" },
    {
      title: "Amount",
      custom: true,
      component: (i) => (
        <div className="text-right">
          {formatNumber(
            i.totalCredit?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
            })
          )}
        </div>
      ),
    },
    {
      title: "Status",
      custom: true,
      component: (i) => (
        <div className="text-center">
          <StatusBadge status={i.status} />
        </div>
      ),
    },
  ];

  const rows = [];
  list?.forEach((p) => {
    if (
      p.account_description &&
      p.account_description
        .toLowerCase()
        .indexOf(form.account_description?.toLowerCase()) === -1
    )
      return;
    else rows.push(p);
  });

  const data = form.account_description ? rows : list || [];
  const totalCredit =
    data && data?.reduce((item, id) => item + parseFloat(id.totalCredit), 0);
  const totalDebit =
    data && data?.reduce((item, id) => item + parseFloat(id.totalDebit), 0);

  const groupedData = data?.reduce((acc, current) => {
    if (
      current &&
      current.account_description !== undefined &&
      current.totalCredit !== undefined
    ) {
      const key = current.account_description;
      const amount = current.totalCredit;

      if (key !== undefined && amount !== undefined) {
        // Check if key and amount are defined before processing
        if (!acc[key]) {
          // If the group doesn't exist, create it and initialize the total
          acc[key] = { key, totalAmount: 0 };
        }

        // Add the current amount to the total for the group
        acc[key].totalAmount += amount;
      }
    }
    return acc;
  }, {});

  // Convert the grouped data object back to an array
  const groupedBal = Object.values(groupedData).filter(
    (grouped) => grouped.totalAmount !== 0
  );

  let fullName =
    selectMembers.firstname +
    " " +
    selectMembers.lastname +
    " " +
    selectMembers.middlename;

  return (
    <>
      {/* {JSON.stringify(data)} */}
      <Card body>
        <CardTitle className="text-center h4 mb-2">
          <div className="row">
            <div className="col-md-3">Summary Transaction History</div>
            <div className="col-md-3">
              <SelectInput
                first_item="Filter by"
                // style={{ width: society.role === "Admin" ? "100%" : "170%" }}
                placeholder="Account Description"
                options={selectDec || []}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    account_description: e.target.value,
                  }))
                }
                value={form.account_description}
                name="account_description"
                width={250}
              />
            </div>
            <div className="col-md-3">
              {society.role === "Admin" && (
                <div className="m-2">
                  <StateListInput
                    label={false}
                    value={form.state}
                    onChange={(e) => {
                      let value = e.target.value;
                      setForm((p) => ({ ...p, state: value }));
                    }}
                  />
                  {/* <MemberListInput
                    label={false}
                    value={form.user_id}
                    onChange={(e) => {
                      let value = e.target.value;
                      const selected = members.find((e) => e.user_id === value);

                      setForm((p) => ({ ...p, user_id: value }));
                      setSelectMembers({ ...selected, id: selected.user_id });
                    }}
                  /> */}
                </div>
              )}
              <div />
            </div>
            <div className="col-md-3">
              <ExportCSV
                csvData={data}
                fileName={`${fullName} transactions history from ${form.from} - ${form.to}`}
              />
            </div>
          </div>
        </CardTitle>
        <div className="row border rounded p-2">
          {groupedBal?.map((item, id) => (
            <div className="col-md-3" key={item.key}>
              {item.key}: {formatNumber(item.totalAmount)}
            </div>
          ))}
        </div>
        <div className="row">
          <div className="col-md-8">
            <DaterangeSelector
              from={form.from}
              to={form.to}
              handleChange={handleRange}
              space={false}
            />
          </div>
          <div className="col-md-4 text-right mt-3">
            Total Debit: <b>{formatNumber(totalDebit)}</b> Total Credit:{" "}
            <b>{formatNumber(totalCredit)}</b>
          </div>
        </div>

        <CustomTable fields={tableFields} data={data || []} />
      </Card>
    </>
  );
}

export default SummaryTransactionHistory;
